/**
 * The `preview.*` commands and their key bindings (W2-02; WORKPACKAGES Appendix B, DESIGN.md §4.8-4.9, §8.2-8.3).
 * Every toggle of the preview toolbar is a command (palette, View menu, toolbar button, shortcut where frequent);
 * the 'preview.context' menu commands act on the line under the right click (the stage records it) or, from the
 * palette, on the active line.
 */

import type { CommandArgs } from '../../app/registry/commands';
import type { CommandContext, CommandDef, CommandSource, KeyBinding, KeymapPresetId } from '../../app/registry/types';
import { Core } from '../../core/commands/index';
import type { LineId } from '../../core/model/types';
import type { Json, MessageRef } from '../../core/util/types';
import { liveServices } from './live';
import { m } from './preview.messages';
import {
  EXT_LANE_LINK,
  EXT_READOUT,
  EXT_SCREEN,
  EXT_TEST_MODE,
  isScreenShape,
  laneLinkOf,
  readExt,
  readoutOf,
  SAFE_AREAS,
  SCREEN_SHAPES,
  testPlayModeOf,
  type SafeArea,
  type ScreenShape,
} from './prefs';
import { cancelParentPick, setParentFlow, startParentPick } from './overlays/selection';
import { peekPreviewStage } from './previewLoop';
import { stageClock, stageStateOf } from './stageState';
import { testPlayOf } from './testPlayState';
import { PREVIEW_FRAME_ID } from '../../render/types';

const ref = (key: string, params?: Readonly<Record<string, string | number>>): MessageRef =>
  params === undefined ? { key } : { key, params };

/** Palette / menu label of a safe-area choice. */
export function safeAreaLabel(area: SafeArea): MessageRef {
  return area === 'none' ? ref(m.safeAreaNone) : ref(m.safeAreaOption, { area });
}

/** Palette / settings label of a screen shape. */
export function screenLabel(shape: ScreenShape): MessageRef {
  if (shape === 'chart') return ref(m.screenOptionChart);
  if (shape === 'fill') return ref(m.screenOptionFill);
  return ref(m.screenOptionAs, { shape });
}

/** Context right clicks are honoured for this long (a menu is open meanwhile). */
const CONTEXT_TTL_MS = 60_000;

function projectOpen(ctx: CommandContext): true | MessageRef {
  return ctx.store !== null && ctx.engine !== null ? true : ref(m.reasonNoProject);
}

function preview(ctx: CommandContext) {
  return ctx.editor.getState().preview;
}

/**
 * Edit-mode overlays and tools (DESIGN.md §4.8: Clean shows only what Phira shows): labels, ghosts, invisible
 * lines, gizmo, auto-key, sample mode, lane link, safe areas and the view zoom are disabled with this reason in
 * Clean, wherever they run from (toolbar, More menu, View menu, palette, shortcut).
 */
function editModeOnly(ctx: CommandContext): true | MessageRef {
  return preview(ctx).mode === 'edit' ? true : ref(m.reasonEditMode);
}

/** Command sources that are a pointer press on a button: they hand no focus back after running. */
const POINTER_SOURCES: ReadonlySet<CommandSource> = new Set<CommandSource>(['button', 'gesture']);

function setPreview(
  ctx: CommandContext,
  patch: Parameters<ReturnType<CommandContext['editor']['getState']>['setPreview']>[0],
): void {
  ctx.editor.getState().setPreview(patch);
  ctx.scheduler.request(PREVIEW_FRAME_ID);
}

/** The line a context-menu command acts on: the right-clicked one, else the active line. */
export function contextLine(ctx: CommandContext): LineId | null {
  if (ctx.source === 'context') {
    const t = stageStateOf(ctx).getSnapshot().contextTarget;
    if (t !== null && Date.now() - t.at < CONTEXT_TTL_MS) return t.line;
  }
  return ctx.editor.getState().activeLine;
}

function toggle(
  id: string,
  title: string,
  icon: string,
  aliases: readonly string[],
  key: 'showHud' | 'lineLabels' | 'hiddenGhosts' | 'showInvisibleLines' | 'gizmo' | 'autoKey' | 'sampleMode',
  order: number,
  extra: Partial<CommandDef> = {},
): CommandDef {
  return {
    id,
    title: title as CommandDef['title'],
    category: 'View',
    icon,
    aliases,
    level: 'guided',
    checked: (ctx) => preview(ctx)[key],
    menus: [{ menu: 'app.view', group: 'preview', order }],
    run(ctx) {
      setPreview(ctx, { [key]: !preview(ctx)[key] });
    },
    ...extra,
  };
}

const theater: CommandDef<CommandArgs['preview.theater']> = {
  id: 'preview.theater',
  title: m.cmdTheater,
  category: 'View',
  icon: 'Maximize',
  aliases: ['theater', 'theatre', 'full window', 'fullscreen', 'maximise preview', 'NAV-06', '全屏'],
  level: 'guided',
  hint: 2,
  enabled: projectOpen,
  checked: (ctx) => preview(ctx).theater,
  menus: [{ menu: 'app.view', group: 'preview', order: 1 }],
  run(ctx) {
    if (testPlayOf(ctx).active) return;
    setPreview(ctx, { theater: !preview(ctx).theater });
  },
};

const testPlay: CommandDef<CommandArgs['preview.testPlay']> = {
  id: 'preview.testPlay',
  title: m.cmdTestPlay,
  category: 'Playback',
  icon: 'Gamepad2',
  aliases: ['test play', 'playtest', 'try', 'autoplay', 'judged', 'F5', '试玩', 'B9'],
  level: 'guided',
  hint: 3,
  enabled: projectOpen,
  args: {
    kind: 'choice',
    label: ref(m.argFrom),
    options: [
      { value: { from: 'playhead' }, label: ref(m.fromPlayhead) },
      { value: { from: 'start' }, label: ref(m.fromStart) },
    ],
  },
  menus: [{ menu: 'app.view', group: 'preview', order: 2 }],
  async run(ctx, args) {
    const from = args?.from === 'start' ? 'start' : 'playhead';
    await testPlayOf(ctx).start(from);
  },
};

const toggleMode: CommandDef = {
  id: 'preview.toggleMode',
  title: m.cmdToggleMode,
  category: 'View',
  icon: 'PenTool',
  aliases: ['clean', 'edit mode', 'overlays', 'clean preview', 'edit preview'],
  level: 'guided',
  checked: (ctx) => preview(ctx).mode === 'edit',
  menus: [{ menu: 'app.view', group: 'preview', order: 3 }],
  run(ctx) {
    const mode = preview(ctx).mode === 'edit' ? 'clean' : 'edit';
    setPreview(ctx, { mode });
    // Remembered per user (DESIGN.md §4.8).
    ctx.settings.set('preview', 'defaultMode', mode);
  },
};

const safeArea: CommandDef<{ readonly area?: SafeArea } | undefined> = {
  id: 'preview.safeArea',
  title: m.cmdSafeArea,
  category: 'View',
  icon: 'Scan',
  aliases: ['safe area', 'guide', '3:2', '4:3', '16:9', 'frame'],
  level: 'guided',
  args: {
    kind: 'choice',
    label: ref(m.cmdSafeArea),
    options: SAFE_AREAS.map((area) => ({ value: { area }, label: safeAreaLabel(area) })),
  },
  enabled: editModeOnly,
  checked: (ctx) => preview(ctx).safeAreas !== 'none',
  menus: [{ menu: 'app.view', group: 'preview', order: 6 }],
  run(ctx, args) {
    const cur = preview(ctx).safeAreas;
    const next = args?.area ?? SAFE_AREAS[(SAFE_AREAS.indexOf(cur) + 1) % SAFE_AREAS.length] ?? 'none';
    setPreview(ctx, { safeAreas: next });
  },
};

const screen: CommandDef<{ readonly shape?: string } | undefined> = {
  id: 'preview.screen',
  title: m.cmdScreen,
  category: 'View',
  icon: 'MonitorSmartphone',
  aliases: ['screen', 'aspect', 'device', '4:3', '21:9', 'phone', 'tablet', 'letterbox'],
  level: 'guided',
  args: {
    kind: 'choice',
    label: ref(m.cmdScreen),
    options: SCREEN_SHAPES.map((shape) => ({ value: { shape }, label: screenLabel(shape) })),
  },
  run(ctx, args) {
    const shape = args?.shape;
    if (shape === undefined || !isScreenShape(shape)) return;
    ctx.settings.setExt(EXT_SCREEN, shape);
    ctx.scheduler.request(PREVIEW_FRAME_ID);
  },
};

const phiraSettings: CommandDef = {
  id: 'preview.phiraSettings',
  title: m.cmdPhiraSettings,
  category: 'View',
  icon: 'Settings2',
  aliases: ['phira settings', 'player settings', 'note size', 'particles', 'double hint', 'msaa', 'culling'],
  level: 'guided',
  run(ctx) {
    ctx.ui.showPanel('chart.preview');
    // The palette, a menu or a context menu hands focus back right after running the command: the popover ignores
    // that focus move for a moment (PhiraSettingsPopover.tsx). A pointer press on a button hands nothing back.
    stageStateOf(ctx).set({ phiraOpen: true, phiraGraceAt: POINTER_SOURCES.has(ctx.source) ? null : stageClock() });
  },
};

const readout: CommandDef = {
  id: 'preview.toggleReadout',
  title: m.cmdReadout,
  category: 'View',
  icon: 'Info',
  aliases: ['line info', 'readout', 'TAB overlay', 'VIEW-07', 'position', 'coordinates'],
  level: 'guided',
  checked: (ctx) => readExt(ctx.settings, EXT_READOUT, readoutOf),
  menus: [{ menu: 'app.view', group: 'preview', order: 20 }],
  run(ctx) {
    ctx.settings.setExt(EXT_READOUT, !readExt(ctx.settings, EXT_READOUT, readoutOf));
  },
};

const resetView: CommandDef = {
  id: 'preview.resetView',
  title: m.cmdResetView,
  category: 'View',
  icon: 'Expand',
  aliases: ['reset zoom', 'fit preview', 'zoom 100%'],
  level: 'guided',
  enabled: editModeOnly,
  run(ctx) {
    const stage = peekPreviewStage(ctx);
    if (stage !== null) stage.resetView();
    else stageStateOf(ctx).set({ view: { zoom: 1, panX: 0, panY: 0 } });
  },
};

function zoomCommand(id: string, title: string, icon: string, factor: number): CommandDef {
  return {
    id,
    title: title as CommandDef['title'],
    category: 'View',
    icon,
    aliases: ['preview zoom', 'zoom preview'],
    level: 'full',
    enabled: editModeOnly,
    run(ctx) {
      const stage = peekPreviewStage(ctx);
      if (stage !== null) stage.zoomBy(factor);
    },
  };
}

const testMode: CommandDef = {
  id: 'preview.toggleJudged',
  title: m.cmdJudged,
  category: 'Playback',
  icon: 'Target',
  aliases: ['judged play', 'autoplay', 'judgement', 'play myself', 'manual play'],
  level: 'guided',
  checked: (ctx) => readExt(ctx.settings, EXT_TEST_MODE, testPlayModeOf) === 'judged',
  run(ctx) {
    const next = readExt(ctx.settings, EXT_TEST_MODE, testPlayModeOf) === 'judged' ? 'autoplay' : 'judged';
    testPlayOf(ctx).setMode(next);
  },
};

const laneLink: CommandDef = {
  id: 'preview.toggleLaneLink',
  title: m.cmdLaneLink,
  category: 'View',
  icon: 'Link2',
  aliases: ['lane link', 'link lane and preview', 'hover marker', 'cross highlight'],
  level: 'full',
  enabled: editModeOnly,
  checked: (ctx) => readExt(ctx.settings, EXT_LANE_LINK, laneLinkOf),
  menus: [{ menu: 'app.view', group: 'preview', order: 12 }],
  run(ctx) {
    ctx.settings.setExt(EXT_LANE_LINK, !readExt(ctx.settings, EXT_LANE_LINK, laneLinkOf));
    ctx.scheduler.request(PREVIEW_FRAME_ID);
  },
};

const forceAspect: CommandDef = {
  id: 'preview.toggleForceAspect',
  title: m.cmdForceAspect,
  category: 'View',
  icon: 'RectangleHorizontal',
  aliases: ['force aspect', 'forceAspectRatio', 'letterbox', 'aspect ratio'],
  level: 'full',
  enabled: projectOpen,
  checked: (ctx) => ctx.store?.doc.pkg.forceAspectRatio === true,
  run(ctx) {
    const store = ctx.store;
    if (store === null) return;
    const on = !store.doc.pkg.forceAspectRatio;
    const out = store.transact({ label: ref(m.txnForceAspect), command: 'preview.toggleForceAspect' }, (tx) => {
      Core.doc.patchPkg(tx, { patch: { forceAspectRatio: on } });
    });
    if (!out.ok) ctx.ui.toast(out.reason, { kind: 'error' });
  },
};

// ---------------------------------------------------------------------------------------------------------------
// Context menu (right click in the preview)
// ---------------------------------------------------------------------------------------------------------------

const addLineHere: CommandDef = {
  id: 'preview.addLineHere',
  title: m.cmdAddLineHere,
  category: 'Lines',
  icon: 'Plus',
  aliases: ['add line', 'new line', 'line here'],
  level: 'guided',
  enabled: projectOpen,
  menus: [{ menu: 'preview.context', group: 'line', order: 10 }],
  run(ctx) {
    const services = liveServices(ctx);
    const store = services.store;
    if (store === null) return;
    const t = ctx.source === 'context' ? stageStateOf(ctx).getSnapshot().contextTarget : null;
    const at: readonly [number, number] = t === null ? [0, 0] : [Math.round(t.rpe[0]), Math.round(t.rpe[1])];
    const out = store.transact({ label: ref(m.txnAddLine), command: 'preview.addLineHere' }, (tx) =>
      Core.lines.addLine(tx, { preset: 'standard', at }),
    );
    if (!out.ok) {
      ctx.ui.toast(out.reason, { kind: 'error' });
      return;
    }
    const editor = ctx.editor.getState();
    editor.setActiveLine(out.value);
    editor.select({ lines: new Set([out.value]), primary: { kind: 'line', id: out.value } });
  },
};

const makeActive: CommandDef = {
  id: 'preview.makeActive',
  title: m.cmdMakeActive,
  category: 'Lines',
  icon: 'MousePointerClick',
  aliases: ['activate line', 'select line', 'current line'],
  level: 'guided',
  enabled: (ctx) => {
    const open = projectOpen(ctx);
    if (open !== true) return open;
    return contextLine(ctx) === null ? ref(m.reasonNoLineHere) : true;
  },
  menus: [{ menu: 'preview.context', group: 'line', order: 20 }],
  run(ctx) {
    const line = contextLine(ctx);
    if (line === null) return;
    const editor = ctx.editor.getState();
    editor.setActiveLine(line);
    editor.select({ lines: new Set([line]), primary: { kind: 'line', id: line } });
  },
};

const setParent: CommandDef = {
  id: 'preview.setParent',
  title: m.cmdSetParent,
  category: 'Lines',
  icon: 'Network',
  aliases: ['parent', 'father', 'attach to', 'rig', 'child'],
  level: 'full',
  enabled: (ctx) => {
    const open = projectOpen(ctx);
    if (open !== true) return open;
    return contextLine(ctx) === null ? ref(m.reasonNoLineHere) : true;
  },
  menus: [{ menu: 'preview.context', group: 'parent', order: 10 }],
  run(ctx) {
    const line = contextLine(ctx);
    if (line === null) return;
    const services = liveServices(ctx);
    if (services.editor.getState().preview.mode !== 'edit') services.editor.getState().setPreview({ mode: 'edit' });
    startParentPick(services, line);
  },
};

const clearParent: CommandDef = {
  id: 'preview.clearParent',
  title: m.cmdClearParent,
  category: 'Lines',
  icon: 'Unlink',
  aliases: ['remove parent', 'detach', 'no father'],
  level: 'full',
  enabled: (ctx) => {
    const open = projectOpen(ctx);
    if (open !== true) return open;
    const line = contextLine(ctx);
    if (line === null) return ref(m.reasonNoLineHere);
    return ctx.store?.doc.lines.get(line)?.parent == null ? ref(m.reasonNoParent) : true;
  },
  menus: [{ menu: 'preview.context', group: 'parent', order: 20 }],
  async run(ctx) {
    const line = contextLine(ctx);
    if (line === null) return;
    cancelParentPick(liveServices(ctx));
    await setParentFlow(liveServices(ctx), line, null);
  },
};

const selectLineNotes: CommandDef = {
  id: 'preview.selectLineNotes',
  title: m.cmdSelectLineNotes,
  category: 'Select',
  icon: 'ListChecks',
  aliases: ['select notes of line', 'all notes on line'],
  level: 'guided',
  enabled: (ctx) => {
    const open = projectOpen(ctx);
    if (open !== true) return open;
    const line = contextLine(ctx);
    if (line === null) return ref(m.reasonNoLineHere);
    return (ctx.store?.idx.notesByLine(line).length ?? 0) > 0 ? true : ref(m.reasonNoNotes);
  },
  menus: [{ menu: 'preview.context', group: 'select', order: 10 }],
  run(ctx) {
    const line = contextLine(ctx);
    const store = ctx.store;
    if (line === null || store === null) return;
    const notes = store.idx.notesByLine(line);
    const editor = ctx.editor.getState();
    editor.setActiveLine(line);
    const first = notes[0];
    editor.select({ notes: new Set(notes), primary: first === undefined ? null : { kind: 'note', id: first } });
    ctx.ui.toast(ref(m.toastSelectedNotes, { count: notes.length }));
  },
};

const clearFilters: CommandDef = {
  id: 'preview.clearFilters',
  title: m.cmdClearFilters,
  category: 'View',
  icon: 'Eye',
  aliases: ['show all lines', 'unsolo', 'unmute', 'undim', 'preview differs'],
  level: 'guided',
  run(ctx) {
    ctx.editor.getState().setFilters({
      hiddenLines: new Set(),
      soloLines: new Set(),
      mutedLayers: new Map(),
      dimOthers: 0,
    });
  },
};

export const PREVIEW_COMMANDS: readonly CommandDef[] = [
  theater,
  testPlay,
  toggleMode,
  toggle(
    'preview.toggleHud',
    m.cmdHud,
    'LayoutDashboard',
    ['hud', 'score', 'combo', 'in-game ui', 'VIEW-09'],
    'showHud',
    4,
  ),
  toggle('preview.toggleLabels', m.cmdLabels, 'Tag', ['labels', 'line ids', 'line names', 'VIEW-13'], 'lineLabels', 5, {
    enabled: editModeOnly,
  }),
  safeArea,
  toggle(
    'preview.toggleGhosts',
    m.cmdGhosts,
    'Ghost',
    ['hidden notes', 'why invisible', 'culled notes'],
    'hiddenGhosts',
    7,
    { enabled: editModeOnly },
  ),
  toggle(
    'preview.toggleInvisibleLines',
    m.cmdInvisibleLines,
    'ScanEye',
    ['invisible lines', 'helper lines', 'show all lines', 'VIEW-03'],
    'showInvisibleLines',
    8,
    { enabled: editModeOnly },
  ),
  toggle(
    'preview.toggleGizmo',
    m.cmdGizmo,
    'Move',
    ['gizmo', 'handles', 'drag line', 'direct manipulation'],
    'gizmo',
    9,
    { enabled: editModeOnly },
  ),
  toggle(
    'preview.toggleAutoKey',
    m.cmdAutoKey,
    'CircleDot',
    ['auto key', 'record', 'autokey', 'keyframe on drag'],
    'autoKey',
    10,
    {
      level: 'full',
      hint: 1,
      enabled: editModeOnly,
    },
  ),
  toggle(
    'preview.toggleSampleMode',
    m.cmdSampleMode,
    'Crosshair',
    ['sample', 'sampling', '取样', 'start end points'],
    'sampleMode',
    11,
    {
      level: 'full',
      enabled: editModeOnly,
    },
  ),
  screen,
  phiraSettings,
  readout,
  resetView,
  zoomCommand('preview.zoomIn', m.cmdZoomIn, 'ZoomIn', 1.25),
  zoomCommand('preview.zoomOut', m.cmdZoomOut, 'ZoomOut', 0.8),
  testMode,
  laneLink,
  forceAspect,
  addLineHere,
  makeActive,
  setParent,
  clearParent,
  selectLineNotes,
  clearFilters,
];

const kb = (chord: string, command: string, extra: Partial<KeyBinding> = {}): KeyBinding => ({
  chord,
  command,
  ...extra,
});
const args = (a: Json): Partial<KeyBinding> => ({ args: a });

/** Bindings shared by both presets (DESIGN.md §8.2: Theater `\`, test play F5 / Ctrl+Enter / Shift+F5, Alt+K). */
const SHARED: readonly KeyBinding[] = [
  kb('Backslash', 'preview.theater'),
  kb('F5', 'preview.testPlay', args({ from: 'playhead' })),
  kb('Ctrl+Enter', 'preview.testPlay', { args: { from: 'playhead' }, when: '!textInput' }),
  kb('Shift+F5', 'preview.testPlay', args({ from: 'start' })),
  kb('Alt+KeyK', 'preview.toggleAutoKey', { altFree: null }),
];

export const PREVIEW_KEYBINDINGS: Readonly<Record<KeymapPresetId, readonly KeyBinding[]>> = {
  default: [...SHARED, kb('Shift+KeyI', 'preview.toggleReadout', { when: '!textInput' })],
  // RPE preset: Tab toggles the line info overlay (VIEW-07); Shift+I stays as well.
  rpe: [
    ...SHARED,
    kb('Shift+KeyI', 'preview.toggleReadout', { when: '!textInput' }),
    kb('Tab', 'preview.toggleReadout', { when: '!textInput && !paste.ghost' }),
  ],
};
